import { useState } from 'react'

import { API_BASE } from '../../config'
import { errorText } from '../../lib/offsite'
import { useSession } from '../../lib/SessionContext'
import { MeHead, dayLabel, useMeData } from './parts'
import { SubmitLeave } from './DelegateSubmit'

/* ============================================================================
   การลาของฉัน — `/me/leave`
   API: GET /api/me/leave?year=YYYY · backend/src/routes/me.js → lib/leave.js

   ใบลา กับ ใบปรับปรุงเวลา (LEAVE_TYPE 999) อยู่ตาราง HR_LEAVE เดียวกัน
   แต่คนละเรื่องกันโดยสิ้นเชิง — 999 ไม่ใช่การลา ไม่ตัดโควต้า จึงแยกสองตาราง
   (ของจริงคนเดียวมีใบปรับเวลา 47 ใบในปีเดียว ถ้าปนกันจะหาใบลาไม่เจอ)
   ============================================================================ */

type Quota = {
  leaveType: string
  desc: string
  dayCanUsed: number
  dayUsed: number
  dayRemain: number
  hasQuotaRow: boolean
}

type Form = {
  recId: number
  rowId: string | null
  desc: string
  workDate: string
  dateStart: string | null
  dateEnd: string | null
  hours: number
  reason: string | null
  status: string
  statusText: string
  statusTone: string
  hasEvidence: boolean
}

type Data = {
  year: number
  years: number[]
  employee: { empCode: string; empName?: string | null }
  quota: Quota[]
  leaves: Form[]
  adjustments: Form[]
  summary: { leaveCount: number; adjustmentCount: number; leaveDays: number }
}

/** statusTone จาก backend ('ok' / 'bad' / อื่น ๆ) → คลาสป้ายสี */
const toneClass = (tone: string) =>
  tone === 'ok' ? 'chip--ok' : tone === 'bad' ? 'chip--bad' : 'chip--warn'

/** '2026-07-27T08:30:00' → '27 ก.ค. 69 08:30' (ตัด T ออก ไม่แปลงโซนเวลา) */
function stamp(iso: string | null): string {
  if (!iso) return '—'
  const [date, time] = iso.split('T')
  return `${dayLabel(date)}${time ? ` ${time.slice(0, 5)}` : ''}`
}

/** ยกเลิกได้เฉพาะใบที่ยังไม่ผ่านการอนุมัติจาก HR (OPEN = ยังไม่ส่ง, REQUEST = รออนุมัติ) */
const CANCELLABLE = new Set(['OPEN', 'REQUEST'])

function FormTable({
  rows,
  empty,
  onCancel,
  cancelling,
  onViewEvidence,
}: {
  rows: Form[]
  empty: string
  onCancel: (rowId: string) => void
  cancelling: string | null
  onViewEvidence: (rowId: string) => void
}) {
  if (rows.length === 0) return <p className="meta">{empty}</p>
  return (
    <div className="table-wrap">
      <table className="data">
        <thead>
          <tr>
            <th>ประเภท</th>
            <th>ตั้งแต่</th>
            <th>ถึง</th>
            <th className="num">ชม.</th>
            <th>เหตุผล</th>
            <th>ไฟล์</th>
            <th>สถานะ</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((f) => (
            <tr key={f.recId}>
              <td className="nowrap">{f.desc}</td>
              <td className="nowrap">{stamp(f.dateStart)}</td>
              <td className="nowrap">{stamp(f.dateEnd)}</td>
              <td className="num">{f.hours || '—'}</td>
              <td className="cell-clip">{f.reason ?? '—'}</td>
              <td className="nowrap">
                {f.hasEvidence && f.rowId ? (
                  <button
                    type="button"
                    className="ghost tiny"
                    onClick={() => onViewEvidence(f.rowId as string)}
                  >
                    ดูไฟล์
                  </button>
                ) : (
                  <span className="meta">—</span>
                )}
              </td>
              <td className="nowrap">
                <span className={`chip ${toneClass(f.statusTone)}`}>{f.statusText}</span>
              </td>
              <td className="nowrap">
                {f.rowId && CANCELLABLE.has(f.status) ? (
                  <button
                    type="button"
                    className="ghost tiny"
                    onClick={() => onCancel(f.rowId as string)}
                    disabled={cancelling === f.rowId}
                  >
                    {cancelling === f.rowId ? 'กำลังยกเลิก…' : 'ยกเลิก'}
                  </button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default function MyLeave() {
  const [year, setYear] = useState('')
  const { call } = useSession()
  const { data, loading, error, reload } = useMeData<Data>(
    'leave',
    year ? `?year=${encodeURIComponent(year)}` : '',
  )
  const [cancelling, setCancelling] = useState<string | null>(null)
  const [cancelError, setCancelError] = useState<string | null>(null)

  async function cancel(rowId: string) {
    if (!window.confirm(`ยืนยันยกเลิกใบเลขที่ ${rowId}?`)) return
    setCancelling(rowId)
    setCancelError(null)
    try {
      await call(`/api/me/leave/${encodeURIComponent(rowId)}/cancel`, { method: 'POST' })
      reload()
    } catch (err) {
      setCancelError(errorText(err))
    } finally {
      setCancelling(null)
    }
  }

  /* เปิดไฟล์แนบในแท็บใหม่ — ต้อง fetch เป็น blob พร้อม cookie (credentials:'include')
     ไม่ใช่ <a href> ตรง ๆ เพราะ endpoint อยู่หลัง auth และโฟลเดอร์ .data เข้าจาก URL ไม่ได้
     (วิธีเดียวกับ PhotoBox ของหน้าประวัติการลงเวลา) */
  async function viewEvidence(rowId: string) {
    setCancelError(null)
    try {
      const res = await fetch(
        `${API_BASE}/api/me/leave/${encodeURIComponent(rowId)}/evidence`,
        { credentials: 'include' },
      )
      if (!res.ok) throw new Error(`เปิดไฟล์ไม่ได้ (HTTP ${res.status})`)
      const url = URL.createObjectURL(await res.blob())
      window.open(url, '_blank', 'noopener')
      // ปล่อยให้ browser ถือ url ไว้จนปิดแท็บ — revoke เร็วไปรูป/pdf จะโหลดไม่ขึ้น
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch (err) {
      setCancelError(errorText(err))
    }
  }

  return (
    <>
      <MeHead
        title="การลาของฉัน"
        meta={data ? `${data.employee.empName ?? ''} · ${data.employee.empCode}` : undefined}
        loading={loading}
        error={error}
        onReload={reload}
      >
        {data && (
          <label className="kv">
            <span className="kv-label">ปี</span>
            <select
              value={String(data.year)}
              onChange={(e) => setYear(e.target.value)}
              disabled={loading}
            >
              {data.years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </label>
        )}
      </MeHead>

      {data && <SubmitLeave empName={data.employee.empName ?? null} onDone={reload} />}

      {data && (
        <>
          <section className="card">
            <h2>โควต้าวันลา</h2>
            {data.quota.length === 0 ? (
              <p className="meta">ยังไม่มีโควต้าของปี {data.year}</p>
            ) : (
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>ประเภท</th>
                      <th className="num">ได้</th>
                      <th className="num">ใช้ไป</th>
                      <th className="num">คงเหลือ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.quota.map((q) => (
                      <tr key={q.leaveType}>
                        <td>
                          {q.desc}
                          {/* ไม่มีแถวโควต้า = ลาได้แต่ระบบไม่ได้ตั้งเพดานไว้ ไม่ใช่ "เหลือ 0" */}
                          {!q.hasQuotaRow && <span className="meta"> (ไม่ได้ตั้งเพดาน)</span>}
                        </td>
                        <td className="num">{q.dayCanUsed}</td>
                        <td className="num">{q.dayUsed}</td>
                        <td className="num">{q.dayRemain}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="card">
            <h2>
              ใบลา <span className="meta">{data.summary.leaveCount} ใบ · {data.summary.leaveDays} วัน</span>
            </h2>
            {cancelError && <p className="error">{cancelError}</p>}
            <FormTable
              rows={data.leaves}
              empty={`ไม่มีใบลาในปี ${data.year}`}
              onCancel={cancel}
              cancelling={cancelling}
              onViewEvidence={viewEvidence}
            />
          </section>

          <section className="card">
            <h2>
              ใบปรับปรุงเวลา <span className="meta">{data.summary.adjustmentCount} ใบ</span>
            </h2>
            <p className="meta">ขอแก้เวลาเข้า–ออก ไม่ใช่การลา และไม่ตัดโควต้าวันลา</p>
            <FormTable
              rows={data.adjustments}
              empty={`ไม่มีใบปรับปรุงเวลาในปี ${data.year}`}
              onCancel={cancel}
              cancelling={cancelling}
              onViewEvidence={viewEvidence}
            />
          </section>
        </>
      )}
    </>
  )
}
